// Shared by TransferQueueService (page thread) and the sync web worker, which
// writes offline-copy snapshots: the queue's IndexedDB schema and record
// shapes. Keep this free of Angular so the worker bundle can import it.
import Dexie, { Table } from 'dexie';

export type TransferOpKind = 'add' | 'delta' | 'set' | 'remove' | 'text';

// One queued change to one transfer line, replayed through /transfer/applyItemOps.
export interface TransferOp {
  id?: number;
  purchaseId: string;
  opId: string;
  accountId: string;
  kind: TransferOpKind;
  value?: number | string | null;
  set?: number;
  fieldKey?: string;
  entryMethod?: string;
  createdAt: string;
  status: 'pending' | 'failed';
  attempts: number;
  error?: string | null;
  errorKey?: string | null;
  // Set at enqueue, so a failed change can still be shown with its transfer's
  // key and location once that transfer is gone from the server.
  transferLabel?: string;
  // Why the transfer refused the change — deleted | complete | canceled |
  // canceling | processing | missing (no such id) — or null for any other
  // refusal (no permission, say), which a retry may still clear.
  transferStatus?: string | null;
  // The server's description of that transfer (key, names, timeDeleted …).
  transferDetail?: any;
}

export class TransferQueueDb extends Dexie {
  ops!: Table<TransferOp, number>;
  purchases!: Table<any, string>;
  purchaseLines!: Table<any, [string, number]>;
  meta!: Table<any, string>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      ops: '++id, purchaseId, opId, status, createdAt',
      purchases: 'purchaseId',
      meta: 'key',
    });
    // v2: the offline copy's lines live in their own rows (head in
    // `purchases`), so a change to a few lines never rewrites a 20k-line blob.
    this.version(2).stores({
      ops: '++id, purchaseId, opId, status, createdAt',
      purchases: 'purchaseId',
      purchaseLines: '[purchaseId+index], purchaseId',
      meta: 'key',
    });
  }
}

export function transferQueueDbName(rfId: string | null): string {
  return 'rfTransferQueue-' + (rfId || 'default');
}

// "IC-352775202100 · 05-Maplecrest": how a transfer is named wherever its
// queued changes outlive the page that made them.
export function transferLabelOf(purchase: any): string {
  if (!purchase) {
    return '';
  }
  const where = purchase.locationName || purchase.customerName || purchase.name || '';
  return [purchase.key, where].filter(part => !!part).join(' · ');
}

// Rows of the per-line offline copy, as both writers store them.
export function purchaseLineRows(purchaseId: string, lines: any[]): any[] {
  return (lines || [])
    .filter(line => line && typeof line.index === 'number')
    .map(line => ({ purchaseId, index: line.index, line }));
}
